
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { TopMenu } from "@/components/TopMenu";

const Settings = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [subscriberData, setSubscriberData] = useState<any>(null);
  const { toast } = useToast();
  const navigate = useNavigate();
  const [onboardingStage, setOnboardingStage] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    emergencyContactName: "",
    emergencyContactEmail: "",
    emergencyContactPhone: "",
    nearestPoliceStation: "",
  });

  useEffect(() => {
    const fetchSubscriberData = async () => {
      setIsLoading(true);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          console.log("No user found, redirecting to login");
          navigate("/login");
          setIsLoading(false); 
          return;
        }

        console.log("Fetching mapping for user email:", user.email);
        const { data: mappingData, error: mappingError } = await supabase
          .from("user_psid_mappings")
          .select("psid")
          .eq("user_email", user.email)
          .maybeSingle(); // Using maybeSingle instead of single

        if (mappingError) {
          console.error("Error fetching PSID:", mappingError);
          return;
        }

        if (!mappingData) {
          console.log("No PSID mapping found for user");
          toast({
            title: "Error",
            description: "No subscriber data found for your account",
            variant: "destructive",
          });
          return;
        }

        console.log("Found PSID:", mappingData.psid);
        const { data: subscriber, error: subscriberError } = await supabase
          .from("subscribers")
          .select("*")
          .eq("psid", mappingData.psid)
          .maybeSingle(); // Using maybeSingle instead of single

        if (subscriberError) {
          console.error("Error fetching subscriber:", subscriberError);
          toast({
            title: "Error",
            description: "Failed to load subscriber data",
            variant: "destructive",
          });
          return;
        }

        if (!subscriber) {
          console.log("No subscriber found for PSID");
          toast({
            title: "Error",
            description: "No subscriber data found",
            variant: "destructive",
          });
          return;
        }

        console.log("Found subscriber data:", subscriber);
        setSubscriberData(subscriber);
        setOnboardingStage(subscriber.onboarding_stage);
        
        setFormData({
          name: subscriber.name || "",
          email: subscriber.email || "",
          phone: subscriber.phone || "",
          emergencyContactName: subscriber.emergency_contact_name || "",
          emergencyContactEmail: subscriber.emergency_contact_email || "",
          emergencyContactPhone: subscriber.emergency_contact_phone || "",
          nearestPoliceStation: subscriber.police_station_name || "", // Map from police_station_name to nearestPoliceStation for UI
        });
      } catch (error) {
        console.error("Error in fetchSubscriberData:", error);
        toast({ title: "Error", description: "An unexpected error occurred while loading data.", variant: "destructive" });
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubscriberData();
  }, [navigate, toast]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (!subscriberData?.subscriber_id) {
        throw new Error("No subscriber ID found");
      }

      const { error } = await supabase.functions.invoke('update-subscriber', {
        body: {
          subscriberData: {
            subscriber_id: subscriberData.subscriber_id,
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            emergency_contact_name: formData.emergencyContactName,
            emergency_contact_email: formData.emergencyContactEmail,
            emergency_contact_phone: formData.emergencyContactPhone,
            police_station_name: formData.nearestPoliceStation, // Map to the correct column in the database
          }
        }
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Your settings have been updated successfully!",
      });
    } catch (error: any) {
      console.error("Update Error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update settings",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <TopMenu onboardingStage={onboardingStage} />
        <div className="container mx-auto px-4 py-8 text-center">Loading settings...</div>
      </div>
    );
 }

 if (!subscriberData && !isLoading) {
  return (
    <div className="min-h-screen bg-background">
      <TopMenu onboardingStage={onboardingStage} />
      <div className="container mx-auto px-4 py-8 text-center text-red-600">
        Could not load subscriber data. Please check notifications or try again later.
      </div>
    </div>
  );
}

  return (
    <div className="min-h-screen bg-background">
      <TopMenu 
        onboardingStage={onboardingStage}
      />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-md mx-auto space-y-8 bg-card p-8 rounded-lg shadow-sm">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-card-foreground">Settings</h2>
            <p className="mt-2 text-sm text-muted-foreground">Update your profile information</p>
          </div>

          <form onSubmit={handleSubmit} className="mt-8 space-y-6">
            <div className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-card-foreground">
                  Name
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Smith"
                  className="mt-1"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-card-foreground">
                  Email
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john@example.com"
                  className="mt-1"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-card-foreground">
                  Phone
                </label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+1234567890"
                  className="mt-1"
                />
              </div>

              <div>
                <label htmlFor="emergencyContactName" className="block text-sm font-medium text-card-foreground">
                  Emergency Contact Name
                </label>
                <Input
                  id="emergencyContactName"
                  name="emergencyContactName"
                  type="text"
                  required
                  value={formData.emergencyContactName}
                  onChange={handleChange}
                  placeholder="Jane Smith"
                  className="mt-1"
                />
              </div>

              <div>
                <label htmlFor="emergencyContactEmail" className="block text-sm font-medium text-card-foreground">
                  Emergency Contact Email
                </label>
                <Input
                  id="emergencyContactEmail"
                  name="emergencyContactEmail"
                  type="email"
                  required
                  value={formData.emergencyContactEmail}
                  onChange={handleChange}
                  placeholder="jane@example.com"
                  className="mt-1"
                />
              </div>

              <div>
                <label htmlFor="emergencyContactPhone" className="block text-sm font-medium text-card-foreground">
                  Emergency Contact Phone
                </label>
                <Input
                  id="emergencyContactPhone"
                  name="emergencyContactPhone"
                  type="tel"
                  required
                  value={formData.emergencyContactPhone}
                  onChange={handleChange}
                  placeholder="+1234567890"
                  className="mt-1"
                />
              </div>

              <div>
                <label htmlFor="nearestPoliceStation" className="block text-sm font-medium text-card-foreground">
                  Nearest Police Station
                </label>
                <Input
                  id="nearestPoliceStation"
                  name="nearestPoliceStation"
                  type="text"
                  required
                  value={formData.nearestPoliceStation}
                  onChange={handleChange}
                  placeholder="Local Police Department"
                  className="mt-1"
                />
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? "Updating..." : "Update Settings"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Settings;
